import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { callsApi } from "@/api/calls.api.ts"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock,
  FileExclamationPoint,
  FileStack,
  Info,
  Languages,
  MapPin,
  Shapes,
  User,
  UserPen,
} from "lucide-react"
import { Separator } from "@/components/ui/separator.tsx"
import { formatDate, getStoredLocale } from "@/lib/utils.ts"
import { CallStatus, StatusBadge } from "@/components/StatusBadge.tsx"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet.tsx"
import {
  Item,
  ItemContent,
  ItemHeader,
  ItemTitle,
} from "@/components/ui/item.tsx"
import { CreatePitchButton } from "@/components/CreatePitchButton.tsx"
import { ErrorCard } from "@/components/ErrorCard.tsx"
import { useUser } from "@/contexts/UserContext.tsx"
import { useTranslation } from "react-i18next"

export const Route = createFileRoute("/_protected/call/$publicId")({
  ssr: false,
  component: RouteComponent,
})

const DetailsContent = ({ call }) => {
  const { t } = useTranslation()
  const locale = getStoredLocale()
  return (
    <div
      dir={locale === "ar" ? "rtl" : "ltr"}
      className={locale === "ar" ? "font-arabic" : ""}
    >
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <FileExclamationPoint size={14} /> {t("details.status")}:
        </span>
        <StatusBadge isCall status={call.status} />
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <Calendar size={14} /> {t("details.posted")}:
        </span>
        <span>{formatDate(call.creationTime)}</span>
      </div>
      <div className="mb-3 flex items-center gap-1 text-primary">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <Clock size={14} /> {t("details.deadline")}:
        </span>
        <span>{formatDate(call.deadline)}</span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <Languages size={14} /> {t("details.lang")}:
        </span>
        <span>
          {(call.languages || [])
            .map((item: { id: number; name: string }) => item.name)
            .join(", ")}
        </span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <MapPin size={14} /> {t("details.loc")}:
        </span>
        <span>
          {call.locations
            .map((item: { id: number; name: string }) => item.name)
            .join(", ")}
        </span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <FileStack size={14} /> {t("details.format")}:
        </span>
        <span>
          {call.formats
            .map((item: { id: number; name: string }) => item.name)
            .join(", ")}
        </span>
      </div>
      <div className="mb-3 flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <Shapes size={14} /> {t("details.themes")}:
        </span>
        <span>
          {call.themes
            .map((item: { id: number; name: string }) => item.name)
            .join(", ")}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <span className="flex min-w-24 flex-row items-center gap-1">
          <CircleDollarSign size={14} /> {t("details.budget")}:
        </span>
        <span>
          {call.budgetFrom} - {call.budgetTo}
        </span>
      </div>
      {call.status === CallStatus.PUBLISHED && (
        <div className="mt-4 w-full">
          <CreatePitchButton className="w-full" callId={call.publicId} />
        </div>
      )}
    </div>
  )
}

function RouteComponent() {
  const { setTitle } = usePageTitle()
  const navigate = useNavigate()
  const { publicId } = useParams({ from: "/_protected/call/$publicId" })
  const [call, setCall] = useState<any>(undefined)
  const { locale } = useUser()
  const isArabic = locale === "ar"
  const { t } = useTranslation()

  const { data, isLoading, error } = useQuery({
    queryKey: ["calls/getByPublicId", publicId],
    queryFn: () => callsApi.getByPublicId(publicId),
    structuralSharing: false,
  })

  useEffect(() => {
    if (data && data.result) {
      setCall(data.result)
    }
  }, [data])

  useEffect(() => {
    setTitle("Call for Pitches")
  }, [setTitle])

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-2">
        <Skeleton className="mb-4 h-8 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="mb-4 h-5 w-1/2" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-1/4" />
      </div>
    )
  }
  if (error) {
    return <ErrorCard backLink="/calls" />
  }

  return call ? (
    <div className="w-full">
      <header className="mb-4">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-secondary-foreground">
            <Button
              variant="ghost"
              size="sm"
              className="p-0 text-secondary-foreground"
              onClick={() => navigate({ to: "/calls" })}
            >
              {isArabic ? <ChevronRight /> : <ChevronLeft />}
              <span className="not-md:hidden">{t("common.back")}</span>
            </Button>
            <Separator
              orientation="vertical"
              className="mr-2 ml-2 not-md:mr-1 not-md:ml-1"
            />
            <div className="flex gap-2 text-xs text-secondary-foreground not-md:flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar size={14} /> {formatDate(call.creationTime)}
              </span>
              <Separator
                orientation="vertical"
                className="mr-2 ml-2 h-8 not-md:hidden"
              />
              <span className="flex items-center gap-1 text-primary">
                <Clock size={14} /> {t("details.deadline")}:{" "}
                {formatDate(call.deadline)}
              </span>
            </div>
          </div>
          <StatusBadge isCall status={call.status} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <h1 className="mt-4 mb-4 font-heading text-2xl">{call.topic}</h1>
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger className="flex items-center">
                <Info className="text-secondary-foreground" />
              </SheetTrigger>
              <SheetContent side="bottom" dir={locale === "ar" ? "rtl" : "ltr"}>
                <SheetHeader>
                  <SheetTitle
                    className={locale === "ar" ? "font-arabic" : "font-sans"}
                  >
                    {" "}
                    {t("details.title")}:
                  </SheetTitle>
                </SheetHeader>
                <SheetDescription className="p-4 pt-0">
                  <DetailsContent call={call} />
                </SheetDescription>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <div className="flex h-full w-full max-w-full flex-row gap-2">
        <div className="w-full lg:pr-3">
          <div
            className="pitch-container w-full wrap-break-word hyphens-auto"
            style={{ wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{ __html: call.description }}
          />
          {call.status === CallStatus.PUBLISHED && (
            <div className="mt-4 flex justify-end not-sm:fixed not-sm:bottom-0 not-sm:left-0 not-sm:w-full not-sm:bg-background not-sm:p-3">
              <CreatePitchButton
                callId={call.publicId}
                className="not-sm:w-full"
              />
            </div>
          )}
        </div>
        <div className="w-md not-lg:hidden">
          <Item variant="muted">
            <ItemHeader className="mb-2">
              <ItemTitle>{t("details.title")}</ItemTitle>
            </ItemHeader>
            <ItemContent className="text-secondary-foreground">
              <DetailsContent call={call} />
            </ItemContent>
          </Item>
        </div>
      </div>
    </div>
  ) : null
}
